<template>
    <app-popover v-if="tooltip && hasTooltip" :wrapper-element="nameOnly ? 'span' : 'div'" ref="popover" container-class="tooltip" arrow class="person-box-user" @open="open"
        :handle-focusable="false">
        <template #default="{mouseenter, mouseleave, toggle, close, isOpen, popoverId}">
            <person-box v-if="!nameOnly" :shape="shape" v-bind="personBoxValues"
                :listener="{mouseenter, mouseleave, click: () => toggle(), focusout: () => close(), keyup: (event) => handleKeyup(event, toggle)}" @contact="contact"
                :show-image="showImage" :id="popoverId" :popover-open="isOpen" :tabindex="focusable ? '0' : '-1'" aria-haspopup="true">
                <template #name="{username}">
                    <slot name="name" :username="_name"><strong>{{_name}}</strong></slot>
                </template>
                <template #default>
                    <slot>
                        <template v-if="showStatus && badge">
                            {{badge.title}}
                        </template>
                    </slot>
                </template>
                <template #image-content>
                    <slot name="image-content">
                        <i v-if="removable && person" class="material-icons text-grey" aria-hidden="true" :tabindex="focusable ? '0' : '-1'"
                            @click.stop="$emit('remove', person)" @keyup.space.prevent="$emit('remove', person)" role="img">close</i>
                        <i v-else-if="badge" :style="{color: badge.color}" :title="badge.title" class="material-icons" aria-hidden="true" role="img">{{badge.icon}}</i>
                    </slot>
                </template>
            </person-box>
            <span v-else v-on="{mouseenter, mouseleave, click: () => toggle(), focusout: () => close(), keyup: (event) => handleKeyup(event, toggle)}" :tabindex="focusable ? '0' : '-1'" aria-haspopup="true">
                <slot name="name" :username="_name"><strong>{{_name}}</strong></slot>
            </span>
        </template>
        <template #container="{popoverId}">
            <loading-indicator v-if="!tooltipText"/>
            <div v-else v-html="tooltipText" :aria-labelledby="popoverId"></div>
        </template>
    </app-popover>
    <person-box v-else :shape="shape" v-bind="personBoxValues" @contact="contact">
        <template #name="{username}">
            <slot name="name" :username="_name"><strong>{{_name}}</strong></slot>
        </template>
        <template #default>
            <slot>
                <template v-if="showStatus && badge">
                    {{badge.title}}
                </template>
            </slot>
        </template>
        <template #image-content>
            <slot name="image-content">
                <i v-if="removable && person" class="material-icons" aria-hidden="true" :tabindex="focusable ? '0' : '-1'"
                    @click.stop="$emit('remove', person)" @keyup.space.prevent="$emit('remove', person)" role="img">close</i>
                <i v-else-if="badge" :style="{color: badge.color}" :title="badge.title" class="material-icons" aria-hidden="true" role="img">{{badge.icon}}</i>
            </slot>
        </template>
    </person-box>
</template>

<script lang="ts">
    import {defineAsyncComponentWrapper, delay} from '@/library/utils';
    import {isStaging} from '@/utils/portuna';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import {namespace} from 'vuex-facing-decorator';
    import {getAbbreviationAvatar, getAvatarByName} from '../filters/username';
    import {contactUser} from '../library/intranet/contact';
    import {isAllowedEmployeeResource} from '../library/user';
    import AppPopover from './AppPopover.vue';
    import LoadingIndicator from './LoadingIndicator.vue';
    import PersonBox from './PersonBox.vue';

    let emptyAvatar: Nullable<string> = null;

    const TelephoneToolbarStore = namespace('telephoneToolbar');

    @Component({
        name: 'PersonBoxUser',
        components: {
            LoadingIndicator,
            AppPopover: defineAsyncComponentWrapper(() => import('./AppPopover.vue')),
            PersonBox,
        },
        emits: ['remove'],
    })
    export class PersonBoxUser extends Vue
    {
        readonly $refs: {
            popover: typeof AppPopover
        };

        @TelephoneToolbarStore.Getter('employees') telephoneToolbarEmployees: TelephoneToolbarEmployees;

        @Prop({
            type: Number,
            required: true,
        }) id: number;

        @Prop({
            type: String,
            default: '',
            required: false,
        }) name: string;

        @Prop({
            type: String,
            default: '',
            required: false,
        }) image: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) hideContact: boolean;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) showName: boolean;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) showImage: boolean;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) showTooltip: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) nameOnly: boolean;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) tooltip: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) removable: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) surnameFirst: boolean;

        @Prop({
            type: String,
            default: '',
            required: false,
        }) popoverId: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) showStatus: boolean;

        @Prop({
            type: String,
            default: 'circle',
            required: false,
            validator(value: string)
            {
                return ['circle', 'square'].includes(value);
            },
        }) shape: string;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) useIntranetColors: boolean;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) focusable: boolean;

        @Watch('showTooltip')
        watchShowTooltip(show: boolean)
        {
            if (!show)
            {
                this.$refs.popover && this.$refs.popover.close();
            }
        }

        @Watch('storePerson', {deep: true})
        watchPerson(newValue: any)
        {
            if (typeof newValue !== 'undefined')
            {
                this.person = newValue;
            }
        }

        loading = true;

        failed = false;

        person: User | null | undefined = null;

        hasTooltip: boolean = true;

        tooltipText: string = '';

        mouseover = false;

        type: PersonBoxTypes = 'user';

        _badge: Nullable<PersonBoxBadge> = null;

        messages = {
            user: {
                unknown: 'Unbekannter Nutzer',
                deleted: 'Gelöschter Benutzer',
                error: 'Der Nutzer konnte nicht gefunden werden.',
            },
            role: {
                unknown: 'Unbekannte Nutzer-Gruppe',
                deleted: 'Gelöschte Nutzer-Gruppe',
                error: 'Die Nutzer-Gruppe konnte nicht gefunden werden.',
            },
            employee: {
                unknown: 'Unbekannter Mitarbeiter',
                deleted: 'Gelöschter Mitarbeiter',
                error: 'Der Mitarbeiter konnte nicht gefunden werden.',
            },
            entity: {
                unknown: 'Unbekannte Abteilung',
                deleted: 'Gelöschte Abteilung',
                error: 'Die Abteilung konnte nicht gefunden werden.',
            },
        };

        get roleOrEntity()
        {
            return ['entity', 'role'].includes(this.type);
        }

        get personBoxValues()
        {
            let values = {
                image: this.avatar,
                name: this._name,
                showName: this.showName,
                showContact: !this.hideContact && !this.roleOrEntity,
                class: {'user-disabled': !this.isActive},
            };

            if (isStaging())
            {
                Object.assign(values, {
                    'data-id': this.id,
                    'data-type': this.type,
                });
            }

            return values;
        }

        get avatar()
        {
            if (this.image)
            {
                return this.image;
            }

            if (this.loading)
            {
                if (!emptyAvatar)
                {
                    emptyAvatar = getAbbreviationAvatar('', this.shape, this.useIntranetColors);
                }

                return emptyAvatar;
            }

            return !this.failed && this.person && this.person.paths && this.person.paths.preview ? this.person.paths.preview : this.defaultAvatar;
        }

        get defaultAvatar(): string
        {
            return getAvatarByName(this.person?.name || '', this.shape, this.useIntranetColors, 40, false, 'role' === this.type);
        }

        get isActive()
        {
            return this.person && this.person.active;
        }

        get personName(): string
        {
            if (this.loading)
            {
                return this.messages[this.type].unknown;
            }

            let name: string;
            if (this.person && this.person.id)
            {
                name = this.surnameFirst && this.person.surname ? this.person.surname + ', ' + this.person.prename : this.person.name;
            }
            else
            {
                name = this.messages[this.type].deleted;
            }

            return name;
        }

        get _name(): string
        {
            if (this.name)
            {
                return this.name;
            }

            let name = this.personName;
            if (this.loading)
            {
                name = 'Name wird geladen';
            }

            return name;
        }

        get hasAbsencesModule()
        {
            return isAllowedEmployeeResource('absences');
        }

        get telephoneToolbarEmployee()
        {
            if (!this.showStatus || 'role' === this.type || 'entity' === this.type || !this.hasAbsencesModule)
            {
                return null;
            }

            for (const employee in this.telephoneToolbarEmployees)
            {
                if (!this.telephoneToolbarEmployees.hasOwnProperty(employee))
                {
                    continue;
                }

                if (
                    'employee' === this.type && this.id === this.telephoneToolbarEmployees[employee].id
                    || 'user' === this.type && this.id === this.telephoneToolbarEmployees[employee].system_id
                )
                {
                    return this.telephoneToolbarEmployees[employee];
                }
            }

            return null;
        }

        get storePerson()
        {
            let stateKey = 'entity' === this.type ? 'entities' : this.type + 's';

            return this.$store['state'][stateKey][this.id];
        }

        get badge()
        {
            if (!this.telephoneToolbarEmployee)
            {
                return null;
            }

            return this.telephoneToolbarEmployee.badge;
        }

        created()
        {
            this.fetch();
        }

        mounted()
        {
            if (this.isExportRequest || !this.tooltip || !this.hasTooltip)
            {
                return;
            }

            if (this.id <= 0 /* (0 = system user, -1 = anonymous) */)
            {
                return;
            }

            (async() => {
                let count = 0;
                while(!this.$refs.popover && count < 5)
                {
                    count++;
                    await delay();
                }

                let popover = this.$refs.popover;

                if (!popover)
                {
                    return;
                }

                popover.emitter.on('mouseleave', () =>
                {
                    this.mouseover = false;
                });

                popover.emitter.on('mouseenter', () =>
                {
                    this.mouseover = true;

                    window.setTimeout(() =>
                    {
                        if (!this.mouseover || !this.showTooltip)
                        {
                            return;
                        }

                        this.fetchTooltip().then(tooltip =>
                        {
                            this.tooltipText = tooltip;
                            this.mouseover && popover.open();
                        })
                        .catch(() =>
                        {
                            this.tooltipText = this.messages[this.type].error;
                        });
                    }, 400);
                });
            })();
        }

        contact()
        {
            contactUser(this.id, this.type);
        }

        open()
        {
            let popover = this.$refs.popover;

            if (!popover || this.isExportRequest || !this.hasTooltip)
            {
                return;
            }

            this.fetchTooltip().then((tooltip) =>
            {
                this.tooltipText = tooltip;
                !popover.popoverVisible.value && popover.open();
            })
            .catch(() =>
            {
                this.tooltipText = this.messages[this.type].error;
            });
        }

        fetch()
        {
            this.loading = true;
            this.failed  = false;

            this.fetchPerson().then((data: User) =>
            {
                this.person = data;
            }).catch(() =>
            {
                this.failed = true;
            }).finally(() =>
            {
                this.loading = false;
            });
        }

        fetchTooltip()
        {
            return this.$store.dispatch(
                'fetchTooltip',
                {
                    id: this.id,
                    type: this.type,
                },
            );
        }

        fetchPerson()
        {
            return this.$store.dispatch(
                'fetchPerson',
                {
                    id: this.id,
                    type: this.type,
                },
            );
        }

        handleKeyup(event: KeyboardEvent, eventHandler: () => void)
        {
            if (event.code !== 'Space')
            {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            eventHandler();
        }
    }

    export default PersonBoxUser;
</script>